import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { defineds } from 'constants/ui.constants';
import {
	SYSTEM_MESSAGE_CONTENT_MAX,
	SYSTEM_MESSAGE_DEFAULT_END_TIME,
	SYSTEM_MESSAGE_DEFAULT_START_TIME,
	SYSTEM_MESSAGE_INTERNAL_DESCRIPTION_MAX,
	SYSTEM_MESSAGE_TITLE_MAX,
	SystemMessageAudienceType,
	SystemMessageIconType,
	type SystemMessageAudienceType as SystemMessageAudienceTypeValue,
	type SystemMessageIconType as SystemMessageIconTypeValue,
} from 'constants/system-messages.constants';
import {
	clearContactIdsValidation,
	getContactIdsValidation,
	getSystemMessagesSaving,
} from 'state/reducers/system-messages.reducer';
import type {
	ICreateSystemMessagePayload,
	ISystemMessageCompanyContact,
	SystemMessageFormMode,
} from 'types/system-messages.types';
import {
	combineDateAndTime,
	getContactIdsFromRaw,
	getHtmlText,
	hasSameContactIds,
} from '../createForm.helpers';

type UseCreateSystemMessageFormParams = {
	mode: SystemMessageFormMode;
	companyId?: number;
	companyContacts: ISystemMessageCompanyContact[];
	onSubmit: (payload: ICreateSystemMessagePayload) => void;
};

export const useCreateSystemMessageForm = ({
	mode,
	companyId,
	companyContacts,
	onSubmit,
}: UseCreateSystemMessageFormParams) => {
	const dispatch = useAppDispatch();
	const contactIdsValidation = useAppSelector(getContactIdsValidation);
	const isSaving = useAppSelector(getSystemMessagesSaving);
	const seededCompanyIdRef = useRef<number | undefined>(undefined);

	const [internalDescription, setInternalDescription] = useState('');
	const [iconType, setIconType] = useState<SystemMessageIconTypeValue>(SystemMessageIconType.GENERAL);
	const [titleHe, setTitleHe] = useState('');
	const [titleEn, setTitleEn] = useState('');
	const [contentHe, setContentHe] = useState('');
	const [contentEn, setContentEn] = useState('');
	const [audienceType, setAudienceType] = useState<SystemMessageAudienceTypeValue>(
		mode === 'company' ? SystemMessageAudienceType.ALL_COMPANY_CONTACTS : SystemMessageAudienceType.ALL_USERS
	);
	const [contactIdsRaw, setContactIdsRaw] = useState('');
	const [checkedContactIds, setCheckedContactIds] = useState<number[]>([]);
	const [dateRange, setDateRange] = useState<{ startDate: Date; endDate: Date }>({
		startDate: defineds.startOfToday,
		endDate: defineds.endOfToday,
	});
	const [startTime, setStartTime] = useState(SYSTEM_MESSAGE_DEFAULT_START_TIME);
	const [endTime, setEndTime] = useState(SYSTEM_MESSAGE_DEFAULT_END_TIME);

	useEffect(() => {
		return () => {
			dispatch(clearContactIdsValidation());
		};
	}, [dispatch]);

	useEffect(() => {
		if (mode !== 'company' || !companyId || companyContacts.length === 0) {
			return;
		}
		if (seededCompanyIdRef.current === companyId) {
			return;
		}
		seededCompanyIdRef.current = companyId;
		setCheckedContactIds(companyContacts.map((contact) => contact.id));
	}, [mode, companyId, companyContacts]);

	useEffect(() => {
		if (audienceType !== SystemMessageAudienceType.SPECIFIC_CONTACTS) {
			dispatch(clearContactIdsValidation());
		}
	}, [audienceType, dispatch]);

	const startDateTime = useMemo(
		() => combineDateAndTime(dateRange.startDate, startTime),
		[dateRange.startDate, startTime]
	);
	const endDateTime = useMemo(() => combineDateAndTime(dateRange.endDate, endTime), [dateRange.endDate, endTime]);
	const isScheduleInvalid = endDateTime <= startDateTime;

	const { formatError: contactIdsFormatError, ids: parsedContactIds } = getContactIdsFromRaw(contactIdsRaw);
	const hasCurrentContactIdsValidation = hasSameContactIds(parsedContactIds, contactIdsValidation.requestedIds);
	const submitContactIds = hasCurrentContactIdsValidation
		? contactIdsValidation.valid.map((contact) => contact.id)
		: [];

	const internalDescriptionTooLong = internalDescription.length > SYSTEM_MESSAGE_INTERNAL_DESCRIPTION_MAX;
	const titleHeTooLong = titleHe.length > SYSTEM_MESSAGE_TITLE_MAX;
	const titleEnTooLong = titleEn.length > SYSTEM_MESSAGE_TITLE_MAX;
	const contentHeText = useMemo(() => getHtmlText(contentHe), [contentHe]);
	const contentEnText = useMemo(() => getHtmlText(contentEn), [contentEn]);
	const contentHeTooLong = contentHeText.length > SYSTEM_MESSAGE_CONTENT_MAX;
	const contentEnTooLong = contentEnText.length > SYSTEM_MESSAGE_CONTENT_MAX;

	const canSave = useMemo(() => {
		if (
			!internalDescription.trim() ||
			!titleHe.trim() ||
			!titleEn.trim() ||
			!contentHeText ||
			!contentEnText ||
			internalDescriptionTooLong ||
			titleHeTooLong ||
			titleEnTooLong ||
			contentHeTooLong ||
			contentEnTooLong
		) {
			return false;
		}

		if (isScheduleInvalid) {
			return false;
		}

		if (audienceType === SystemMessageAudienceType.SPECIFIC_CONTACTS) {
			if (
				contactIdsFormatError ||
				!hasCurrentContactIdsValidation ||
				contactIdsValidation.invalid.length > 0 ||
				submitContactIds.length === 0
			) {
				return false;
			}
		}

		if (audienceType === SystemMessageAudienceType.SPECIFIC_COMPANY_CONTACTS && checkedContactIds.length === 0) {
			return false;
		}

		return true;
	}, [
		audienceType,
		checkedContactIds.length,
		contactIdsFormatError,
		contactIdsRaw,
		contactIdsValidation.invalid.length,
		contactIdsValidation.requestedIds,
		contentEnText,
		contentEnTooLong,
		contentHeText,
		contentHeTooLong,
		isScheduleInvalid,
		hasCurrentContactIdsValidation,
		internalDescription,
		internalDescriptionTooLong,
		startDateTime,
		submitContactIds.length,
		titleEn,
		titleEnTooLong,
		titleHe,
		titleHeTooLong,
	]);

	const handleCompanyContactToggle = (contactId: number, checked: boolean) => {
		setCheckedContactIds((prev) => (checked ? [...prev, contactId] : prev.filter((id) => id !== contactId)));
	};

	const handleSave = () => {
		if (!canSave) {
			return;
		}

		const payload: ICreateSystemMessagePayload = {
			internalDescription: internalDescription.trim(),
			iconType,
			titleHe: titleHe.trim(),
			titleEn: titleEn.trim(),
			contentHe,
			contentEn,
			audienceType,
			startDate: startDateTime.toISOString(),
			endDate: endDateTime.toISOString(),
		};

		if (audienceType === SystemMessageAudienceType.SPECIFIC_CONTACTS) {
			payload.contactIds = submitContactIds;
		}

		if (audienceType === SystemMessageAudienceType.SPECIFIC_COMPANY_CONTACTS) {
			payload.contactIds = checkedContactIds;
		}

		if (mode === 'company' && companyId) {
			payload.targetCompanyId = companyId;
		}

		onSubmit(payload);
	};

	return {
		isSaving,
		canSave,
		isScheduleInvalid,
		handleSave,
		internalDescription,
		setInternalDescription,
		internalDescriptionTooLong,
		iconType,
		setIconType,
		titleHe,
		setTitleHe,
		titleHeTooLong,
		titleEn,
		setTitleEn,
		titleEnTooLong,
		contentHe,
		setContentHe,
		contentHeTooLong,
		contentEn,
		setContentEn,
		contentEnTooLong,
		audienceType,
		setAudienceType,
		contactIdsRaw,
		setContactIdsRaw,
		checkedContactIds,
		handleCompanyContactToggle,
		companyContacts,
		dateRange,
		setDateRange,
		startTime,
		setStartTime,
		endTime,
		setEndTime,
	};
};
